import React, { useEffect, useRef, useState } from "react";
import { formatISTTime } from "../utils/dateTime";
import { MessageCircle, Trash2, X, Send } from "lucide-react";
import "../css/Chatwindow.css";

const WS_URL = "wss://app.himalayanpetclinic.com/v1/ws/notifications";
const HEARTBEAT_INTERVAL_MS = 45000;
const RECONNECT_DELAY_MS = 3000;

const ChatWindow = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [connected, setConnected] = useState(false);

    const socketRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messageInputRef = useRef(null);
    const notificationSoundRef = useRef(null);
    const heartbeatTimerRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const shouldReconnectRef = useRef(true);

    const playNotificationSound = () => {
        const sound = notificationSoundRef.current;
        if (!sound) return;

        sound.currentTime = 0;
        sound.play().catch(() => {
            // Browsers only allow audio after the user has interacted with the page.
        });
    };

    useEffect(() => {
        shouldReconnectRef.current = true;

        const clearHeartbeat = () => {
            window.clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
        };

        const connect = () => {
            clearHeartbeat();
            const socket = new WebSocket(WS_URL);
            socketRef.current = socket;

            socket.onopen = () => {
                setConnected(true);
                heartbeatTimerRef.current = window.setInterval(() => {
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: "HEARTBEAT" }));
                    }
                }, HEARTBEAT_INTERVAL_MS);
            };

            socket.onmessage = (event) => {

            let data;

            try {
                data = JSON.parse(event.data);
            } catch {
                data = { message: event.data };
            }

            if (data?.type === "HEARTBEAT") {
                return;
            }

            const receivedMessage = String(data?.message ?? "").trim();

            if (!receivedMessage) {
                return;
            }

            setIsOpen(true);
            setMessages((prev) => [
                ...prev,
                {
                    sender: data.sender || "User",
                    message: receivedMessage,
                    time: new Date(),
                    isNew: true
                }
            ]);
            playNotificationSound();
            };

            socket.onclose = () => {
                clearHeartbeat();
                setConnected(false);

                if (shouldReconnectRef.current) {
                    reconnectTimerRef.current = window.setTimeout(connect, RECONNECT_DELAY_MS);
                }
            };

            socket.onerror = () => {
                setConnected(false);
            };
        };

        connect();

        return () => {
            shouldReconnectRef.current = false;
            clearHeartbeat();
            window.clearTimeout(reconnectTimerRef.current);
            socketRef.current?.close();
        };

    }, []);

    useEffect(() => {

        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth"
        });

    }, [messages]);

    const openChat = () => {
        setIsOpen(true);
    };

    const closeChat = () => {
        setIsOpen(false);
    };

    const clearChat = () => {
        setMessages([]);
    };

    useEffect(() => {
        if (isOpen) {
            messageInputRef.current?.focus();
        }
    }, [isOpen, messages.length]);

    const sendMessage = () => {

        const text = message.trim();

        if (!text) {
            return;
        }

        if (
            !socketRef.current ||
            socketRef.current.readyState !== WebSocket.OPEN
        ) {
            return;
        }

        const chatMessage = {
            type: "CHAT_MESSAGE",
            sender: "Doctor",
            message: text
        };

        socketRef.current.send(
            JSON.stringify(chatMessage)
        );

        setMessage("");
    };

    const handleKeyDown = (event) => {

        if (
            event.key === "Enter" &&
            !event.shiftKey
        ) {
            event.preventDefault();
            sendMessage();
        }
    };

    return (
        <>
            <audio
                ref={notificationSoundRef}
                src="/mixkit-software-interface-back-2575.wav"
                preload="auto"
            />
            {/* CHAT WINDOW */}

            {isOpen && (
                <div className="chat-window">

                    <div className="chat-header">

                        <div>
                            <div className="chat-title">
                                Clinic Chat
                            </div>

                            <div className="chat-status">
                                <span
                                    className={
                                        connected
                                            ? "status-dot online"
                                            : "status-dot offline"
                                    }
                                />

                                {connected
                                    ? "Connected"
                                    : "Disconnected"}
                            </div>
                        </div>

                        <div className="chat-header-actions">
                            {messages.length > 0 && (
                                <button
                                    type="button"
                                    className="chat-clear"
                                    onClick={clearChat}
                                    aria-label="Clear chat messages"
                                    title="Clear chat"
                                >
                                    <Trash2 size={16} />
                                    <span>Clear</span>
                                </button>
                            )}

                            <button
                                type="button"
                                className="chat-close"
                                onClick={closeChat}
                                aria-label="Close clinic chat"
                            >
                                <X size={18} />
                            </button>
                        </div>

                    </div>

                    <div className="chat-messages">

                        {messages.length === 0 && (
                            <div className="chat-empty">
                                No messages yet
                            </div>
                        )}

                        {messages.map((item, index) => (
                            <div
                                key={index}
                                className={`chat-message ${item.isNew ? "chat-message-new" : ""}`}
                            >

                                <div className="chat-message-meta">
                                    <div className="chat-sender">
                                        {item.sender}
                                    </div>
                                    {item.isNew && <span className="chat-new-label">New</span>}
                                </div>

                                <div className="chat-text">
                                    {item.message}
                                </div>

                                <div className="chat-time">
                                    {formatISTTime(item.time)}
                                </div>

                            </div>
                        ))}

                        <div ref={messagesEndRef} />

                    </div>

                    <div className="chat-input-area">

                        <textarea
                            ref={messageInputRef}
                            value={message}
                            onChange={(e) =>
                                setMessage(e.target.value)
                            }
                            onKeyDown={handleKeyDown}
                            placeholder="Type a message..."
                            rows={1}
                        />

                        <button
                            type="button"
                            className="chat-send-button"
                            onClick={sendMessage}
                            disabled={
                                !connected ||
                                !message.trim()
                            }
                        >
                            <Send size={16} />
                        </button>

                    </div>

                </div>
            )}

            {!isOpen && (
                <div className="chat-launcher">
                    <button
                        type="button"
                        className="chat-floating-button"
                        onClick={openChat}
                        title="Open chat"
                        aria-label="Open clinic chat"
                    >
                        <MessageCircle size={24} />
                    </button>
                </div>
            )}

        </>
    );
};

export default ChatWindow;
